<script setup lang="ts">
import SearchFormContent from "@/components/UnexpectedEventAndAlertStatuseList/SearchFormContent.vue";
import { SELECTABLE_STATUSES } from '@/services/consts/unecpected_event_and_alert_status';
import { Ref, reactive, ref, computed, watch } from 'vue';
import i18n from '@/lang/i18n';
import { getDateRangeDays } from '@/services/functions';
import { getDatetimeWithoutSeconds } from "@/services/functions";

const MAX_FILTER_DAYS = 21;

const props = defineProps<{
  isLoading: boolean
  isManagementOffice: boolean
  isSubManagementOffice: boolean
  isOperationOffice: boolean
  isDepartment: boolean
}>()

const emits = defineEmits(['search', 'formchange'])
const hide: Ref<boolean> = ref(false)
const form: any = reactive({
  dateRange: [
    new Date(),
    new Date()
  ],
  includes_start_or_end_time: 'start_time',
  division: SELECTABLE_STATUSES[0],
})
const errors: any = reactive({})

const title = computed(() => {
  return hide.value ? "" : i18n.global.t('pages.common.filter.title')
})

const searchAreaClass = computed(() => {
  return hide.value ? "search-area-hide p-4" : "search-area p-4"
})

const operationOfficeClearable = computed(() => {
  return props.isManagementOffice || props.isSubManagementOffice
})

function clearForm () {
  form.dateRange = [
    new Date(),
    new Date()
  ]
  form.includes_start_or_end_time = 'start_time'
  form.division = SELECTABLE_STATUSES[0]
  form.managementOffice = props.isManagementOffice ? undefined : form.managementOffice
  form.operationOffice = operationOfficeClearable.value ? undefined : form.operationOffice
  form.group = undefined
  form.department = undefined
  form.vehicle = undefined
  form.driver = undefined
}

function toggleSidebar () {
  hide.value = !hide.value
}

const changeSearchButton = computed(() => {
  return props.isLoading ? i18n.global.t('pages.common.button.loading') : i18n.global.t('pages.common.button.exec')
})

function handleClickSearch() {
  if (validateForm()) return
  emits('search', form, true)
}

function validateForm() {
  clearErrors()
  if (!form.operationOffice) errors.operationOffice = [i18n.global.t('pages.common.filter.errorMssages.pleaseSelect')]
  if (!form.dateRange[0] || !form.dateRange[1] || getDatetimeWithoutSeconds(form.dateRange[0]) > getDatetimeWithoutSeconds(form.dateRange[1])) {
    errors.start_time = [i18n.global.t('pages.common.filter.errorMssages.pleaseSelectCorrectRange')]
  } else if (getDateRangeDays(form.dateRange[0], form.dateRange[1]) > MAX_FILTER_DAYS) {
    errors.start_time = [`${i18n.global.t('pages.common.filter.errorMssages.range')}${MAX_FILTER_DAYS}${i18n.global.t('pages.common.filter.errorMssages.pleaseSelectWithinDays')}`]
  }
  return (errors.operationOffice?.length || errors.start_time?.length)
}

function clearErrors() {
  errors.operationOffice = []
  errors.start_time = []
}

function makeInitialForms() {
  if (props.isOperationOffice || props.isDepartment) {
    form.start_time = new Date()
    form.start_time.setDate(form.start_time.getDate() - 3)
  }
}

watch(form, () => {
  emits('formchange', form)
})
watch(() => props.isOperationOffice, () => {
  makeInitialForms()
});
watch(() => props.isDepartment, () => {
  makeInitialForms()
});
</script>
<template>
  <div :class="searchAreaClass">
    <h4 class="d-flex justify-content-between mt-0">
      {{ title }}
      <svg
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        fit=""
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        sandboxuid="0"
        @click="toggleSidebar"
      >
        <template v-if="hide">
          <path d="M7.615 11.795l4.59-4.59-1.41-1.41-6 6 6 6 1.41-1.41-4.59-4.59zm9.59 6v-12h-2v12h2z" fill-rule="evenodd" sandboxuid="0"></path>
        </template>
        <template v-if="!hide">
          <path d="M6 7.41L10.438 12 6 16.59 7.363 18l5.802-6-5.802-6L6 7.41zM16.066 6H18v12h-1.934V6z" sandboxuid="0" />
        </template>
      </svg>
    </h4>
    <div :class="`strpied-tabled-with-hover ${hide ? 'd-none' : '' }`">
      <div class="d-grid gap-2">
        <SearchFormContent
          v-model="form"
          :maxFilterDays="MAX_FILTER_DAYS"
          :errors="errors"
          :operationOfficeClearable="operationOfficeClearable"
          :isManagementOffice="props.isManagementOffice"
          :isDepartment="props.isDepartment"
        />
      </div>
      <div class="d-flex flex-column">
        <div class="col-md-3">
          <svg
            version="1.2"
            style="overflow: visible; width: 299px; height: 1px; opacity: 1; mix-blend-mode: normal; rotate(0deg);"
          >
            <defs>
              <path id="path-1650848226989195" d="M1126 703 C1126 703 1425 703 1425 703"></path>
            </defs>
            <g transform="translate(-1126, -703)">
              <path style="stroke: rgb(194, 186, 186); stroke-width: 1; stroke-linecap: butt; stroke-linejoin: miter; fill: none;" d="M1126 703 C1126 703 1425 703 1425 703"></path>
            </g>
          </svg>
        </div>
        <div class="d-flex search-buttons mx-auto" >
          <v-btn
            class="ma-2 exec-btn"
            @click="handleClickSearch"
            :disabled="props.isLoading"
            :style="{
              backgroundColor : props.isLoading ? '#B3B8BB' : ''
            }"
          >
            {{ changeSearchButton }}
          </v-btn>
          <v-btn
            class="ma-2 reset-btn"
            :disabled="props.isLoading"
            @click="clearForm"
          >
            {{ i18n.global.t('pages.common.button.reset') }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>
